import {
  log,
  type NativeEventPropertiesV10,
  type NativeEventV10,
} from "@indeed/signals-schema/com/indeed/signals/schema/jsma/NativeEventV10";
import { ProviderId } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/ProviderId";
import type { ViewJobHint } from "@indeed/tare/cache/JSTCache";
import { JSTOrientation, type JSTDeviceModelRecord } from "@indeed/tare/model/JSTDeviceModel";
import type { ProctorAllocation } from "@indeed/tare/proctor/JSTProctor";
import { z } from "zod";

import type { RestResponse } from "../../../viewjob/native-module/types/ViewJobNativeModule";
import type { NativeGraphQLFetchResult } from "../../apollo/ApolloNativeModule";
import { normalizeEnvironment } from "../../environment/normalizeEnvironment";
import type { CanonicalEnvironment } from "../../environment/types";
import { getJSTReporter } from "../../error-reporting/JSTReporter";
import { getErrorMessage } from "../../logging/DatadogErrorUtils";
import type { RawTypedNativeModules } from "../types/NativeModulesTypes";
import { CookieKey } from "../types/RNCoreNativeModule";
import type { TareHostContext } from "../types/TareHostContext";
import { processExtraParams } from "./processExtraParams";
import { proxyBridges } from "./proxyBridges";

declare global {
  var _tareData: TareHostContext;
  var _tareMethods: {
    onScroll: (scrollX: number, scrollY: number) => void;
    close: (tag?: number) => void;
  };
  var _rootProps: { preloadedVJData: model.viewjob.ViewJobClientSideModel } | undefined;
  var _initialData:
    | {
        viewJobClientSideModel?: model.viewjob.ViewJobClientSideModel;
        ssr: boolean;
        // Injected at the top level by the JSF rnw-onegraph page template
        oneGraphApiUrl?: string;
        oneGraphApiKey?: string;
        deploymentGroup?: string;
        deploymentGroupToken?: string;
      }
    | undefined;
  // Apollo cache the server serializes into the page for client rehydration.
  // Present only on Apollo-SSR'd pages.
  var __APOLLO_STATE__: Record<string, unknown> | undefined;
}

const platform = "ANDROID"; // TODO: Update to WEB after signals schema updated - jira/MOSPLAT-3996

type WebProctorAllocation = ProctorAllocation & { allocationId?: string };

// Stored hints may come from any client version. Preserve unknown keys while
// independently falling malformed or missing known fields back to null.
const viewJobHintSchema = z
  .object({
    isSaved: z.boolean().nullable().catch(null),
    title: z.string().nullable().catch(null),
    isFromAce: z.boolean().nullable().catch(null),
    jobResultTrackingKey: z.string().nullable().catch(null),
    isDisliked: z.boolean().nullable().catch(null),
    matchFactor: z.string().nullable().catch(null),
  })
  .passthrough();

const getProctorLoggingString = (): string =>
  Object.entries(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Web allocations may include the JSF allocation ID.
    (globalThis._tareData.proctorAllocations ?? {}) as Record<string, WebProctorAllocation>,
  )
    .filter(([, { value }]) => value !== -1)
    .map(([testName, { value, allocationId }]) => {
      const bucketAssignment = `${testName}${value}`;
      return allocationId && allocationId !== "force"
        ? `${allocationId}:${bucketAssignment}`
        : bucketAssignment;
    })
    .join(",");

export const RNTareBridge = {
  getAppModel: () => Promise.resolve(RNTareBridge.getAppModelSync()),
  getAppModelSync: () => {
    const buildEnvironment: CanonicalEnvironment = normalizeEnvironment(
      globalThis._tareData.stagingLevel,
    );
    return {
      appVersion: "web-1.0.0",
      buildConfig: "debug",
      buildEnvironment,
      locale: {
        languageCode: globalThis._tareData.languageCode,
        countryCode: globalThis._tareData.countryCode,
      },
    } as const;
  },
  getAccountStatus: () => Promise.resolve(RNTareBridge.getAccountStatusSync()),
  getAccountStatusSync: () => ({
    isLoggedIn: globalThis._tareData.isLoggedIn,
    id: globalThis._tareData.id,
    accountId: globalThis._tareData.accountId,
    emailAddress: globalThis._tareData.emailAddress,
    isConfirmed: globalThis._tareData.isConfirmed,
    creationDate: globalThis._tareData.creationDate,
  }),
  getDeviceModel: () => {
    getJSTReporter(ProviderId.Unknown).info("getDeviceModel is not fully implemented on web");
    return Promise.resolve(RNTareBridge.getDeviceModelSync());
  },
  getDeviceModelSync: () => ({
    ctk: globalThis._tareData.ctk,
    deviceId: null,
    manufacturer: "web",
    manufacturerModel: globalThis.navigator.userAgent,
    get orientation(): JSTDeviceModelRecord["orientation"] {
      return globalThis.screen.orientation.type === "portrait-primary"
        ? JSTOrientation.PORTRAIT
        : JSTOrientation.LANDSCAPE;
    },
    // @ts-expect-error userAgentData is not yet in the standard Navigator type.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    osVersion: globalThis.navigator.userAgentData?.platform ?? "web",
  }),
  logTapButton: async (screenName: string, elementName: string) => {
    await RNTareBridge.logTapButtonWithExtraParams(screenName, elementName, [], {});
  },
  logTapButtonWithExtraParams(
    screenName: string,
    elementName: string,
    tags: string[],
    extraParams: Record<string, string | number | boolean>,
  ): Promise<void> {
    const { stringParams, stringValues, intParams, intValues } = processExtraParams(extraParams);
    stringParams.push("screenName", "elementName");
    stringValues.push({ string: screenName }, { string: elementName });
    const properties: NativeEventPropertiesV10 = {
      appId: "TEST",
      appVersion: "web-1.0.0",
      appBuildNum: 0,
      appVariant: "WEB",
      appEnvironment: globalThis._tareData.stagingLevel,
      appCountry: { string: globalThis._tareData.countryCode ?? "" },
      clientEventId: "WEB",
      appInstallId: "WEB",
      appSessionId: "WEB",
      sessionId: "WEB",
      clientCreatedTimestamp: Date.now(),
      accountId: { string: globalThis._tareData.accountId ?? "" },
      groups: globalThis._tareData.proctorAllocations
        ? Object.entries(globalThis._tareData.proctorAllocations).map(
            ([testName, { value }]) => testName + value,
          )
        : [],
      ctk: { string: globalThis._tareData.ctk ?? "" },
      deviceOrientation: "LANDSCAPE",
    };
    const event: NativeEventV10 = {
      device: { platform },
      properties,
      eventType: "interaction",
      eventName: "tapButton",
      tags,
      eventVersion: 1,
      stringParams,
      stringValues,
      intParams,
      intValues,
    };
    log(event);
    return Promise.resolve();
  },
  logScreenView: async (screenName: string) => {
    await RNTareBridge.logScreenViewWithExtraParams(screenName, [], {});
  },
  logScreenViewWithExtraParams(
    screenName: string,
    tags: string[],
    extraParams: Record<string, string | number | boolean>,
  ): Promise<void> {
    const { stringParams, stringValues, intParams, intValues } = processExtraParams(extraParams);
    stringParams.push("autoTrackedLastScreenName");
    stringValues.push({ string: screenName });
    const properties: NativeEventPropertiesV10 = {
      appId: "TEST",
      appVersion: "web-1.0.0",
      appBuildNum: 0,
      appVariant: "WEB",
      appEnvironment: globalThis._tareData.stagingLevel,
      appCountry: { string: globalThis._tareData.countryCode ?? "" },
      clientEventId: "WEB",
      appInstallId: "WEB",
      appSessionId: "WEB",
      sessionId: "WEB",
      clientCreatedTimestamp: Date.now(),
      accountId: { string: globalThis._tareData.accountId ?? "" },
      groups: globalThis._tareData.proctorAllocations
        ? Object.entries(globalThis._tareData.proctorAllocations).map(
            ([testName, { value }]) => testName + value,
          )
        : [],
      ctk: { string: globalThis._tareData.ctk ?? "" },
      deviceOrientation: "LANDSCAPE",
    };
    const event: NativeEventV10 = {
      device: { platform },
      properties,
      eventType: "impression",
      eventName: "screenView",
      tags,
      eventVersion: 1,
      stringParams,
      stringValues,
      intParams,
      intValues,
    };
    log(event);
    return Promise.resolve();
  },
  async openShareSheet(url: string, message: string, subject: string | null | undefined) {
    if (globalThis.navigator?.share) {
      try {
        await globalThis.navigator.share({ url, text: message, title: subject ?? undefined });
        return { action: "sharedAction" as const };
      } catch (error) {
        // AbortError means the user dismissed the share sheet.
        if (error instanceof Error && error.name === "AbortError") {
          getJSTReporter(ProviderId.Unknown).info("openShareSheet dismissed");
        } else {
          getJSTReporter(ProviderId.Unknown).warn(
            `openShareSheet failed: ${getErrorMessage(error)}`,
            { containsPII: true },
          );
        }
      }
    } else {
      getJSTReporter(ProviderId.Unknown).warn("`openShareSheet` not implemented");
    }
    return { action: "dismissedAction" as const };
  },
  getProctorAllocations(testNames: string[]): Promise<Record<string, ProctorAllocation>> {
    return Promise.resolve(
      Object.fromEntries(
        Object.entries(globalThis._tareData.proctorAllocations ?? {}).filter(([testName]) =>
          testNames.includes(testName),
        ),
      ),
    );
  },
  getProctorLoggingString: () => Promise.resolve(getProctorLoggingString()),
  getProctorLoggingStringSync: getProctorLoggingString,
  startNativeFetch(
    _requestId: string,
    _url: string,
    _method: string,
    _body: string | null,
    _headers?: Record<string, string> | null,
  ): Promise<{ status: number; headersJson: string }> {
    return Promise.reject(new Error("startNativeFetch not available on web"));
  },
  getAppSessionInfo: () => Promise.resolve(null),
  getAppSessionInfoSync: () => null,
  getCachedViewJobHint(key: string): Promise<ViewJobHint | null> {
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(`TareCache.${key}`) ?? "null");
      if (stored === null) return Promise.resolve(null);
      const result = viewJobHintSchema.safeParse(stored);
      if (!result.success) {
        getJSTReporter(ProviderId.Unknown).warn(
          `[getCachedViewJobHint] Ignoring unreadable cached hint for key: ${key}`,
        );
        return Promise.resolve(null);
      }
      return Promise.resolve(result.data);
    } catch {
      return Promise.resolve(null);
    }
  },
  setCachedViewJobHint(key: string, hint: ViewJobHint): Promise<void> {
    try {
      localStorage.setItem(`TareCache.${key}`, JSON.stringify(hint));
    } catch {
      // Nothing actionable when browser storage is unavailable.
    }
    return Promise.resolve();
  },
};

let hasReportedMissingOnScroll = false;

const RNCoreNativeModule = {
  onScroll(scrollX: number, scrollY: number): void {
    if (globalThis._tareMethods.onScroll) {
      globalThis._tareMethods.onScroll(scrollX, scrollY);
    } else if (!hasReportedMissingOnScroll) {
      // Called on every scroll event, so report once per page load.
      hasReportedMissingOnScroll = true;
      getJSTReporter(ProviderId.Unknown).warn("`onScroll` not implemented for web");
    }
  },

  async close(tag: number): Promise<void> {
    if (globalThis._tareMethods.close) {
      globalThis._tareMethods.close(tag);
    } else {
      getJSTReporter(ProviderId.Unknown).warn("`close` not implemented for web");
    }
    return Promise.resolve();
  },

  aceNewConversation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    content?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    agentType?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    optionalArgs?: { [_: string]: string },
  ): Promise<void> {
    getJSTReporter(ProviderId.Unknown).warn("aceNewConversation not implemented for web");
    return Promise.resolve();
  },

  async getCookies<K extends CookieKey>(
    cookieNames: readonly K[],
  ): Promise<Record<K, string | null>> {
    if (typeof document === "undefined" || !document.cookie) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      return Object.fromEntries(cookieNames.map((name) => [name, null])) as Record<
        K,
        string | null
      >;
    }
    const allCookies = new Map(
      document.cookie.split(";").map((cookie): [string, string] => {
        const [name, ...rest] = cookie.trim().split("=");
        return [name, rest.join("=").replace(/^"(.*)"$/, "$1")];
      }),
    );

    const result = Object.fromEntries(
      cookieNames.map((name) => [name, allCookies.get(name) ?? null]),
    );

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return Promise.resolve(result as Record<K, string | null>);
  },

  async setCookies<K extends CookieKey>(cookies: Array<{ name: K; value: string }>): Promise<void> {
    if (typeof document === "undefined") {
      return Promise.resolve();
    }
    for (const cookie of cookies) {
      globalThis.document.cookie = `${cookie.name}=${cookie.value}`;
    }
    return Promise.resolve();
  },

  async deleteCookies<K extends CookieKey>(cookieNames: readonly K[]): Promise<void> {
    if (typeof document === "undefined") {
      return Promise.resolve();
    }
    for (const name of cookieNames) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
    return Promise.resolve();
  },

  /*
        This is only referenced from OneGraphApolloClientProvider, where it's passed as a param to
        createOneGraphApolloClient. It turns out that there's already a web implementation of createOneGraphApolloClient
        that just doesn't use the fetch param at all.
         */
  async fetchNativeOneGraphOperation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    queryString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    variablesJSONString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    operationName: string | null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options?: string,
  ): Promise<NativeGraphQLFetchResult> {
    return Promise.resolve({ data: {} });
  },

  /*
        This is only referenced from OneGraphApolloClientProvider, where it's passed as a param to
        createOneGraphApolloClient. It turns out that there's already a web implementation of createOneGraphApolloClient
        that just doesn't use the fetch param at all.
         */
  async fetchTrackedOneGraphOperation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    queryString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    variablesJSONString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    operationName: string | null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    headers?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options?: string,
  ): Promise<NativeGraphQLFetchResult> {
    return Promise.resolve({ data: {} });
  },
  async mutateTrackedOneGraphOperation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    variablesJSONString: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    operationName: string | null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    headers?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options?: string,
  ): Promise<NativeGraphQLFetchResult> {
    return Promise.resolve({ data: {} });
  },
};

const ViewJobModule = {
  getVJData(url: string): Promise<RestResponse<model.viewjob.ViewJobClientSideModel>> {
    // Attempt to side-load data from the initial page load before going to the server
    const vjData =
      window._rootProps?.preloadedVJData ?? window._initialData?.viewJobClientSideModel;
    if (vjData) {
      return Promise.resolve({ body: vjData });
    }
    // CAUTION: We're blindly asserting that the server response is the expected type.
    // This isn't actually type-safe.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return fetch(url).then((res) => res.json()) as Promise<
      RestResponse<model.viewjob.ViewJobClientSideModel>
    >;
  },
  httpGetJson<T>(url: string): Promise<T> {
    // CAUTION: We're blindly asserting that the server response is the expected type.
    // This isn't actually type-safe.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return fetch(url).then((res) => res.json()) as Promise<T>;
  },
  handleStartIndeedApply<T>(_: number, applyUrl: string): Promise<T> {
    try {
      const hostname = new URL(applyUrl).hostname;
      if (hostname.endsWith("smartapply.indeed.com")) {
        globalThis.location.href = applyUrl;
      } else {
        globalThis.open(applyUrl, "_blank");
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      return Promise.resolve(true as unknown as T);
    } catch (e) {
      return Promise.reject(new Error("Invalid apply URL"));
    }
  },
  async updateApplyStartCookie<T>(
    jobKey: string,
    source: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    origin: string,
  ): Promise<T> {
    const JOB_KEY_SEPARATOR = ":";
    const currentApplyStarts = await RNCoreNativeModule.getCookies([CookieKey.applyStartCookie]);
    const currentApplyStartsList =
      currentApplyStarts && currentApplyStarts.RJAS
        ? currentApplyStarts.RJAS.split(JOB_KEY_SEPARATOR)
        : [];

    const newEncodedJobKey = source + jobKey;
    const filteredCurrentApplyStartsList = currentApplyStartsList.filter((value) => {
      // Handle variable-length prefixes by checking if value ends with jobKey
      return !value.endsWith(jobKey);
    });
    filteredCurrentApplyStartsList.push(newEncodedJobKey);
    if (filteredCurrentApplyStartsList.length > 10) {
      filteredCurrentApplyStartsList.shift();
    }
    const newEncodedJobKeyList = filteredCurrentApplyStartsList.join(JOB_KEY_SEPARATOR);
    const thirtyDaysInMillis = 30 * 24 * 3600 * 1000;
    const expiryTime = Date.now() + thirtyDaysInMillis;
    const expiryDate = new Date(expiryTime);
    // code.corp.indeed.com/jobseeker/jobseeker-frontend/-/blob/packages/jobsearch-ui/web/static/basecamp/src/util/setCookie.ts?L18
    const cookieValue = `${newEncodedJobKeyList};expires=${expiryDate.toUTCString()}`;

    await RNCoreNativeModule.setCookies([
      {
        name: CookieKey.applyStartCookie,
        value: cookieValue,
      },
    ]);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return Promise.resolve(true as unknown as T);
  },
  navigateView<T>(): Promise<T> {
    // Views don't exist for web
    return Promise.reject(new Error("Not implemented for web"));
  },
  httpPostJson<T>(url: string, payload: string): Promise<T> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: payload,
    }).then((res) => res.json()) as Promise<T>;
  },
  async toggleSaveJob(jobKey: string, isSaved: boolean): Promise<void> {
    const UpdateSavedJobStateMutation = `
                        mutation updateSavedJobState($jobKey: String!, $state: SavedJobState!) {
                            updateSavedJobState(jobKey: $jobKey, state: $state) {
                                jobKey
                                state
                            }
                        }
                    `;
    const state = isSaved ? "VISITED" : "SAVED";
    const variables = {
      jobKey,
      state,
    };
    try {
      await RNCoreNativeModule.mutateTrackedOneGraphOperation(
        UpdateSavedJobStateMutation,
        JSON.stringify(variables),
        "updateSavedJobState",
      );
      return Promise.resolve();
    } catch (err) {
      getJSTReporter(ProviderId.Unknown).error(err, {
        errDomain: "toggleSaveJob",
        containsPII: true,
      });
      return Promise.resolve();
    }
  },
  logVJUnmount(): Promise<void> {
    return Promise.resolve();
  },
};

export const RawNativeModules: RawTypedNativeModules = {
  RNJSTNavigator: undefined,
  RNTareBridge,
  RNCoreNativeModule,
  ViewJobModule,
};

export default proxyBridges(RawNativeModules);
